import type {
  Audience,
  AudienceType,
  Pixel,
  Production,
  Rule,
  RuleGroup,
  WebEventType,
} from '@/api/types';
import {
  AUDIENCE_AGE_RANGE_VALUES,
  AUDIENCE_CREDIT_SCORE_VALUES,
  AUDIENCE_EDUCATION_VALUES,
  AUDIENCE_GENDER_VALUES,
  AUDIENCE_INCOME_BANDS,
  AUDIENCE_RELIGION_VALUES,
  INSTALL_PARAMS,
} from '@/api/types.gen';
import {
  decodeAudienceRuleGroups,
  encodeAudienceRuleGroups,
  type EncodedAudienceRules,
} from './audience-rule-codec';
import { purchaseWindowFromValue, purchaseWindowIssue, type PurchaseWindow } from './purchase-window';

export type VisualAudienceType = 'Direct' | 'Retargeting' | 'Lookalike';

export interface VisualAudienceRule {
  field: string;
  operator: string;
  value: unknown;
}

export interface VisualAudienceGroup {
  id?: string;
  op: 'AND';
  rules: VisualAudienceRule[];
}

export interface AudienceRuleResolutionContext {
  productions?: readonly Pick<Production, 'id' | 'name'>[];
  audiences?: readonly Pick<Audience, 'id' | 'name'>[];
  pixels?: readonly Pick<Pixel, 'id' | 'description' | 'domains' | 'status'>[];
}

export class AudienceVisualRuleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AudienceVisualRuleError';
  }
}

// The pixel installation catalog is the source of truth for actions accepted
// by sam('track', ...). Keep the exact wire case: `web_events` stores and
// filters these values byte-for-byte, in PostgreSQL and in the lake alike.
const instructionActions = INSTALL_PARAMS
  .find((param) => param.key === 'pixel_id')
  ?.events ?? [];

export const PIXEL_RETARGETING_ACTIONS: readonly WebEventType[] = [
  ...new Set(instructionActions),
].filter((action): action is WebEventType =>
  action === 'Pageview' || action === 'Purchase');

// Canonical enriched-filter values verified against
// `universe.person_consumer`. Categorical entries are submitted byte-for-byte;
// age/income labels are the stable wire bands compiled to numeric bounds by
// the batch worker. Presentation-only aliases belong in the screen.
export const ENRICHED_AUDIENCE_VALUES = {
  ageRanges: AUDIENCE_AGE_RANGE_VALUES,
  gender: AUDIENCE_GENDER_VALUES,
  incomeBands: AUDIENCE_INCOME_BANDS,
  creditScore: AUDIENCE_CREDIT_SCORE_VALUES,
  education: AUDIENCE_EDUCATION_VALUES,
  religion: AUDIENCE_RELIGION_VALUES,
} as const;

const DAY_WINDOWS: Readonly<Record<string, number>> = {
  '7 days': 7,
  '30 days': 30,
  '60 days': 60,
  '90 days': 90,
  '6 months': 180,
  '1 year': 365,
  '2 years': 730,
};

function asRecord(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new AudienceVisualRuleError('This rule is missing its required values');
  }
  return value as Record<string, unknown>;
}

function nonEmpty(value: unknown, label: string): string {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) throw new AudienceVisualRuleError(`${label} is required`);
  return text;
}

function exactCategoricalValues(
  value: unknown,
  label: string,
  allowed: readonly string[],
): string[] {
  const selected = Array.isArray(value) ? value : [value];
  const values = [...new Set(selected.map((entry) => nonEmpty(entry, label)))];
  if (values.length === 0) {
    throw new AudienceVisualRuleError(`At least one ${label.toLowerCase()} value is required`);
  }
  const unknown = values.find((entry) => !allowed.includes(entry));
  if (unknown !== undefined) {
    throw new AudienceVisualRuleError(`Unsupported ${label.toLowerCase()} value: ${unknown}`);
  }
  return values;
}

// The only published slices a lookalike audience may select, in basis points. Mirrors
// `shared::LOOKALIKE_TOP_SHARE_BPS_STOPS`; keeping the whole set here matters because the
// backend rejects in-range values such as 3 just as it rejects values above the maximum.
export const LOOKALIKE_TOP_SHARE_BPS_STOPS = [1, 2, 5, 10, 20, 50] as const;
export const MAX_LOOKALIKE_TOP_SHARE_BPS =
  LOOKALIKE_TOP_SHARE_BPS_STOPS[LOOKALIKE_TOP_SHARE_BPS_STOPS.length - 1]!;

// The stored bps value as the visual editor's own display string.
//
// `positiveInteger` reads the leading digits back, so this round-trips: the unit suffix is
// presentation and never reaches the wire.
export function formatTopShareBps(bps: number): string {
  return `${bps} bps`;
}

function positiveInteger(value: unknown, label: string): number {
  const number = typeof value === 'number'
    ? value
    : Number.parseInt(String(value).match(/^\s*(\d+)/)?.[1] ?? '', 10);
  if (!Number.isInteger(number) || number <= 0) {
    throw new AudienceVisualRuleError(`${label} must be a positive whole number`);
  }
  return number;
}

export function daysFromVisualWindow(value: unknown): number {
  const label = nonEmpty(value, 'Lookback window').toLowerCase();
  const known = DAY_WINDOWS[label];
  if (known !== undefined) return known;

  const match = label.match(/^(\d+)\s+(day|days|month|months|year|years)$/);
  if (!match) {
    throw new AudienceVisualRuleError(`Unsupported lookback window: ${label}`);
  }
  const quantity = Number(match[1]);
  const unit = match[2]!;
  if (quantity <= 0) {
    throw new AudienceVisualRuleError('Lookback window must be positive');
  }
  return quantity * (unit.startsWith('month') ? 30 : unit.startsWith('year') ? 365 : 1);
}

function visualWindowFromDays(days: number): string {
  const exact = Object.entries(DAY_WINDOWS).find(([, value]) => value === days);
  return exact?.[0] ?? `${days} days`;
}

function parseZipPrefixes(value: unknown): string[] {
  const raw = Array.isArray(value)
    ? value.map(String)
    : String(value ?? '').split(',');
  const zipPrefixes = [...new Set(raw.map((part) => part.trim()).filter(Boolean))];
  if (zipPrefixes.length === 0) {
    throw new AudienceVisualRuleError('At least one ZIP prefix is required');
  }
  if (zipPrefixes.some((prefix) => !/^\d{1,5}$/.test(prefix))) {
    throw new AudienceVisualRuleError('ZIP prefixes must contain 1–5 digits');
  }
  return zipPrefixes;
}

/** A legacy range rule that no set of bands spells, kept whole until the row is changed. */
export interface LegacyRangeValue {
  legacy: Extract<Rule, { kind: 'age' } | { kind: 'income' }>;
}

export function isLegacyRangeValue(value: unknown): value is LegacyRangeValue {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const legacy = (value as { legacy?: unknown }).legacy;
  return legacy !== null && typeof legacy === 'object'
    && ((legacy as { kind?: unknown }).kind === 'age' || (legacy as { kind?: unknown }).kind === 'income');
}

/** "Custom range 30–40" for a legacy rule the bands cannot show. */
export function legacyRangeLabel(value: LegacyRangeValue): string {
  const rule = value.legacy;
  if (rule.kind === 'age') {
    return rule.max >= 120 ? `Custom range ${rule.min}+` : `Custom range ${rule.min}–${rule.max}`;
  }
  return `Custom range ${rule.min_band} – ${rule.max_band}`;
}

/** An age band's years: `25-34` → 25..34; `65+` → 65..120, the legacy ceiling. */
function ageBandBounds(band: string): { min: number; max: number } | null {
  const plus = band.match(/^(\d+)\+$/);
  if (plus) return { min: Number(plus[1]), max: 120 };
  const range = band.match(/^(\d+)-(\d+)$/);
  return range ? { min: Number(range[1]), max: Number(range[2]) } : null;
}

/** The bands a legacy age range spells exactly, or null when its edges are not band edges. */
export function ageBandsForRange(min: number, max: number): string[] | null {
  const bounds = AUDIENCE_AGE_RANGE_VALUES.map((band) => ageBandBounds(band));
  const first = bounds.findIndex((entry) => entry?.min === min);
  const last = bounds.findIndex((entry) => entry !== null && (entry.max === max || (entry.max >= 120 && max >= 120)));
  if (first < 0 || last < first) return null;
  return AUDIENCE_AGE_RANGE_VALUES.slice(first, last + 1);
}

/** The income bands from `minBand` through `maxBand`, or null when either is unknown. */
export function incomeBandsForRange(minBand: string, maxBand: string): string[] | null {
  const bands: readonly string[] = AUDIENCE_INCOME_BANDS;
  const first = bands.indexOf(minBand);
  const last = bands.indexOf(maxBand);
  if (first < 0 || last < first) return null;
  return bands.slice(first, last + 1);
}

/** Selected bands in catalog order, each once: one selection has one spelling, so picking
 * 55–64 then 25–34 is the same definition as the other way round (the server requires it). */
function catalogOrderBands(value: unknown, label: string, catalog: readonly string[]): string[] {
  const selected = new Set(
    (Array.isArray(value) ? value : [value]).map((entry) => nonEmpty(entry, label)),
  );
  const unknown = [...selected].find((entry) => !catalog.includes(entry));
  if (unknown !== undefined) {
    throw new AudienceVisualRuleError(`Unsupported ${label.toLowerCase()} value: ${unknown}`);
  }
  const bands = catalog.filter((band) => selected.has(band));
  if (bands.length === 0) {
    throw new AudienceVisualRuleError(`Select at least one ${label.toLowerCase()} band`);
  }
  return bands;
}

/** A window as API fields, refusing one the server would refuse. */
function resolvedWindow(value: unknown): PurchaseWindow {
  const window = purchaseWindowFromValue(value);
  const issue = purchaseWindowIssue(window);
  if (issue) throw new AudienceVisualRuleError(issue);
  return window;
}

function productionId(
  value: unknown,
  context: AudienceRuleResolutionContext,
): string {
  const id = nonEmpty(value, 'Production');
  const choices = context.productions ?? [];
  if (choices.length > 0 && !choices.some((production) => production.id === id)) {
    throw new AudienceVisualRuleError('Select a production from the current production list');
  }
  return id;
}

function productionIds(
  value: unknown,
  context: AudienceRuleResolutionContext,
): string[] {
  const values = Array.isArray(value) ? value : [value];
  const ids = [...new Set(values.map((entry) => productionId(entry, context)))];
  if (ids.length === 0) {
    throw new AudienceVisualRuleError('At least one production is required');
  }
  return ids;
}

function audienceId(
  value: unknown,
  context: AudienceRuleResolutionContext,
): string {
  const id = nonEmpty(value, 'Seed audience');
  if (!/^[a-z0-9_-]{1,64}$/.test(id)) {
    throw new AudienceVisualRuleError('Select a valid saved-audience ID');
  }
  const choices = context.audiences ?? [];
  if (choices.length > 0 && !choices.some((audience) => audience.id === id)) {
    throw new AudienceVisualRuleError('Select a seed from the current audience list');
  }
  return id;
}

function pixelId(
  value: unknown,
  context: AudienceRuleResolutionContext,
): string {
  const id = nonEmpty(value, 'Pixel');
  if (!/^[a-z0-9_-]{1,64}$/.test(id)) {
    throw new AudienceVisualRuleError('Select a valid pixel');
  }
  const choices = context.pixels ?? [];
  if (choices.length > 0 && !choices.some((pixel) => pixel.id === id)) {
    throw new AudienceVisualRuleError('Select a pixel from the current pixel list');
  }
  return id;
}

function pixelAction(value: unknown): WebEventType {
  const action = nonEmpty(value, 'Pixel action');
  if (!PIXEL_RETARGETING_ACTIONS.includes(action as WebEventType)) {
    throw new AudienceVisualRuleError(
      'Select an action allowed by the pixel installation instructions',
    );
  }
  return action as WebEventType;
}

function resolveLeaf(
  visual: VisualAudienceRule,
  context: AudienceRuleResolutionContext,
): Rule | null {
  switch (visual.field) {
    case 'production_history': {
      const values = asRecord(visual.value);
      const ids = productionIds(values.productionIds ?? values.productionId, context);
      const window = resolvedWindow(values.window);
      // Key order is the server's field order, and an open end is omitted as the server
      // omits it: a campaign reuses its audience only on identical JSON.
      return window.mode === 'last'
        ? { kind: 'purchase_history', production_ids: ids, within_days: window.days }
        : {
          kind: 'purchase_history_between',
          production_ids: ids,
          from: window.from,
          ...(window.to ? { to: window.to } : {}),
        };
    }
    case 'recency': {
      const window = resolvedWindow(visual.value);
      return window.mode === 'last'
        ? { kind: 'recency', within_days: window.days }
        : { kind: 'last_purchase_between', from: window.from, ...(window.to ? { to: window.to } : {}) };
    }
    case 'frequency': {
      const values = asRecord(visual.value);
      return {
        kind: 'frequency',
        min_purchases: positiveInteger(values.minPurchases, 'Minimum purchases'),
        within_days: daysFromVisualWindow(values.window),
      };
    }
    case 'geo':
      return { kind: 'geo', zip_prefixes: parseZipPrefixes(visual.value) };
    case 'age': {
      if (isLegacyRangeValue(visual.value)) return visual.value.legacy;
      return {
        kind: 'age_bands',
        bands: catalogOrderBands(visual.value, 'Age range', ENRICHED_AUDIENCE_VALUES.ageRanges),
      };
    }
    case 'gender': {
      return {
        kind: 'gender',
        // Enrichment values are opaque database categories. Preserve their
        // exact spelling/case instead of normalizing them in the browser.
        values: exactCategoricalValues(
          visual.value,
          'Gender',
          ENRICHED_AUDIENCE_VALUES.gender,
        ),
      };
    }
    case 'income': {
      if (isLegacyRangeValue(visual.value)) return visual.value.legacy;
      let value = visual.value;
      // A draft written before the bands held `{ minBand, maxBand }`.
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        const values = asRecord(value);
        value = incomeBandsForRange(String(values.minBand ?? ''), String(values.maxBand ?? ''));
        if (!value) throw new AudienceVisualRuleError('Select income bands from the supported list');
      }
      return {
        kind: 'income_bands',
        bands: catalogOrderBands(value, 'Household income', ENRICHED_AUDIENCE_VALUES.incomeBands),
      };
    }
    case 'credit_score':
      return {
        kind: 'credit_score',
        values: exactCategoricalValues(
          visual.value,
          'Credit score',
          ENRICHED_AUDIENCE_VALUES.creditScore,
        ),
      };
    case 'education':
      return {
        kind: 'education',
        values: exactCategoricalValues(
          visual.value,
          'Education',
          ENRICHED_AUDIENCE_VALUES.education,
        ),
      };
    case 'religion':
      return {
        kind: 'religion',
        values: exactCategoricalValues(
          visual.value,
          'Religion',
          ENRICHED_AUDIENCE_VALUES.religion,
        ),
      };
    case 'household_has_children': {
      if (typeof visual.value !== 'boolean') {
        throw new AudienceVisualRuleError('Household has children must be true or false');
      }
      return { kind: 'household_has_children', value: visual.value };
    }
    case 'pixel_action': {
      const values = asRecord(visual.value);
      return {
        kind: 'pixel_action',
        pixel_id: pixelId(values.pixelId, context),
        action: pixelAction(values.action),
        within_days: daysFromVisualWindow(values.window),
      };
    }
    case 'web_visit': {
      const values = asRecord(visual.value);
      return {
        kind: 'web_visit',
        domain: nonEmpty(values.term, 'Website domain'),
        within_days: daysFromVisualWindow(values.window),
      };
    }
    case 'web_action': {
      const values = asRecord(visual.value);
      return {
        kind: 'web_action',
        event_name: nonEmpty(values.term, 'Website event'),
        within_days: daysFromVisualWindow(values.window),
      };
    }
    case 'seed':
    case 'simil':
      return null;
    case 'unsupported':
      throw new AudienceVisualRuleError(
        'This saved audience contains a rule that the visual editor cannot safely change',
      );
    default:
      throw new AudienceVisualRuleError(`Unsupported audience rule: ${visual.field}`);
  }
}

function resolveVisualGroup(
  group: VisualAudienceGroup,
  ordinal: number,
  context: AudienceRuleResolutionContext,
): { id: string; op: 'AND'; rules: Rule[] } {
  if (group.op !== 'AND') {
    throw new AudienceVisualRuleError(`Segment ${ordinal + 1} must use AND`);
  }

  const seeds = group.rules.filter((rule) => rule.field === 'seed');
  const similarities = group.rules.filter((rule) => rule.field === 'simil');
  if (seeds.length > 1 || similarities.length > 1) {
    throw new AudienceVisualRuleError(
      `Segment ${ordinal + 1} can contain only one lookalike definition`,
    );
  }
  const seed = seeds[0];
  const similarity = similarities[0];
  if ((seed === undefined) !== (similarity === undefined)) {
    throw new AudienceVisualRuleError(
      `Segment ${ordinal + 1} needs both a seed audience and similarity`,
    );
  }

  const rules = group.rules
    .map((rule) => resolveLeaf(rule, context))
    .filter((rule): rule is Rule => rule !== null);

  if (seed && similarity) {
    // Basis points, not percent. The model scores the whole ~266M-row consumer universe, so
    // 1% is already 2.7M people; the useful range lies entirely below what a percentage can
    // express. Only the published stops are valid; accepting an in-range value such as 3 would
    // let a loaded/programmatic definition pass the browser and fail as a 422 on Save.
    const topShareBps = positiveInteger(similarity.value, 'Top share');
    if (!LOOKALIKE_TOP_SHARE_BPS_STOPS.some((stop) => stop === topShareBps)) {
      throw new AudienceVisualRuleError(
        `Top share must be one of ${LOOKALIKE_TOP_SHARE_BPS_STOPS.join(', ')} bps`,
      );
    }
    rules.push({
      kind: 'lookalike',
      seed: audienceId(seed.value, context),
      top_share_bps: topShareBps,
    });
  }

  if (rules.length === 0) {
    throw new AudienceVisualRuleError(`Segment ${ordinal + 1} needs at least one rule`);
  }

  return {
    id: group.id?.trim() || `segment-${ordinal + 1}`,
    op: 'AND',
    rules,
  };
}

/**
 * Resolve visual rows to the only rule shape accepted by the audience API.
 * Top-level visual groups remain OR branches; leaves inside each branch remain
 * AND predicates. `segments` is suitable for creating/reporting one reusable
 * audience per visual branch without reinterpreting the tree.
 */
export function resolveVisualAudienceRules(
  groups: readonly VisualAudienceGroup[],
  context: AudienceRuleResolutionContext = {},
): EncodedAudienceRules {
  return encodeAudienceRuleGroups(
    groups.map((group, ordinal) => resolveVisualGroup(group, ordinal, context)),
  );
}

/**
 * The stored definition these groups were decoded from, while they are still that very
 * decode: any edit makes new groups. Encoding always writes the canonical tree (an `any` root
 * of `all` groups, which estimates, unions and creates rely on); this is what a caller that
 * REUSES an existing audience compares instead, so an untouched legacy tree (a bare `all`, an
 * `any` of unwrapped leaves, a hand-made range) still matches the row it came from.
 */
const decodedFrom = new WeakMap<readonly VisualAudienceGroup[], RuleGroup>();

export function originalRules(groups: readonly VisualAudienceGroup[]): RuleGroup | undefined {
  return decodedFrom.get(groups);
}

function unsupportedRule(rule: Rule): VisualAudienceRule {
  return {
    field: 'unsupported',
    operator: 'cannot edit',
    value: rule,
  };
}

function visualLeaves(rule: Rule): VisualAudienceRule[] {
  switch (rule.kind) {
    case 'purchase_history':
      return [{
        field: 'production_history',
        operator: 'is any of',
        value: {
          productionIds: rule.production_ids,
          window: { mode: 'last', days: rule.within_days } satisfies PurchaseWindow,
        },
      }];
    case 'purchase_history_between':
      return [{
        field: 'production_history',
        operator: 'is any of',
        value: {
          productionIds: rule.production_ids,
          window: { mode: 'between', from: rule.from, to: rule.to ?? null } satisfies PurchaseWindow,
        },
      }];
    case 'recency':
      return [{
        field: 'recency',
        operator: 'was',
        value: { mode: 'last', days: rule.within_days } satisfies PurchaseWindow,
      }];
    case 'last_purchase_between':
      return [{
        field: 'recency',
        operator: 'was',
        value: { mode: 'between', from: rule.from, to: rule.to ?? null } satisfies PurchaseWindow,
      }];
    case 'frequency':
      return [{
        field: 'frequency',
        operator: 'is at least',
        value: {
          minPurchases: rule.min_purchases,
          window: visualWindowFromDays(rule.within_days),
        },
      }];
    case 'geo':
      return [{ field: 'geo', operator: 'is any of', value: rule.zip_prefixes.join(', ') }];
    case 'age':
      // Edges that are band edges open as those bands; any other range stays whole.
      return [{
        field: 'age',
        operator: 'is any of',
        value: ageBandsForRange(rule.min, rule.max) ?? { legacy: rule } satisfies LegacyRangeValue,
      }];
    case 'age_bands':
      return [{ field: 'age', operator: 'is any of', value: rule.bands }];
    case 'gender':
      return [{
        field: 'gender',
        operator: 'is',
        value: rule.values.map((value) =>
          value[0] ? value[0].toUpperCase() + value.slice(1) : value),
      }];
    case 'income':
      return [{
        field: 'income',
        operator: 'is any of',
        value: incomeBandsForRange(rule.min_band, rule.max_band) ?? { legacy: rule } satisfies LegacyRangeValue,
      }];
    case 'income_bands':
      return [{ field: 'income', operator: 'is any of', value: rule.bands }];
    case 'credit_score':
      return [{
        field: 'credit_score',
        operator: 'is any of',
        value: rule.values,
      }];
    case 'education':
      return [{
        field: 'education',
        operator: 'is any of',
        value: rule.values,
      }];
    case 'religion':
      return [{
        field: 'religion',
        operator: 'is any of',
        value: rule.values,
      }];
    case 'household_has_children':
      return [{
        field: 'household_has_children',
        operator: 'is',
        value: rule.value,
      }];
    case 'pixel_action':
      return [{
        field: 'pixel_action',
        operator: 'for',
        value: {
          pixelId: rule.pixel_id,
          action: rule.action,
          window: visualWindowFromDays(rule.within_days),
        },
      }];
    case 'web_visit':
    case 'web_action':
      // These legacy shapes do not contain a pixel id. Guessing one from a
      // domain or applying an action across every pixel would make the new
      // selector lie, so require an explicit replacement instead.
      return [unsupportedRule(rule)];
    case 'lookalike':
      return [
        { field: 'seed', operator: 'from', value: rule.seed },
        { field: 'simil', operator: 'top', value: formatTopShareBps(rule.top_share_bps) },
      ];
    case 'subscriber_tier':
    case 'group':
      return [unsupportedRule(rule)];
  }
}

/** Decode a persisted API definition for the visual editor without mock data. */
export function visualGroupsFromCanonical(root: RuleGroup): VisualAudienceGroup[] {
  const groups = decodeAudienceRuleGroups(root).map((group): VisualAudienceGroup => {
    const lookalikeCount = group.rules.filter((rule) => rule.kind === 'lookalike').length;
    return {
      id: group.id,
      op: 'AND',
      rules: group.rules.flatMap((rule) =>
        rule.kind === 'lookalike' && lookalikeCount > 1
          ? [unsupportedRule(rule)]
          : visualLeaves(rule)),
    };
  });
  decodedFrom.set(groups, root);
  return groups;
}

/**
 * The audience type a rule tree implies.
 *
 * A port of the server's own predicate — `validate_audience_definition` and the
 * per-type leaf allowlists in `validate_write_tree` — so the type posted with a
 * set of rules is the one the server would have derived anyway. The tab stays a
 * declared choice because it selects the rule VOCABULARY before any rule
 * exists; this is what stops the declaration and the tree disagreeing once they
 * do, which is otherwise a 422 the operator cannot act on.
 */
export function audienceTypeFromRules(root: RuleGroup): AudienceType {
  const leaves = (group: RuleGroup): Rule[] =>
    group.rules.flatMap((rule) => (rule.kind === 'group' ? leaves(rule) : [rule]));
  const flat = leaves(root);
  if (flat.some((rule) => rule.kind === 'lookalike')) return 'lookalike';
  if (flat.length > 0 && flat.every((rule) => rule.kind === 'pixel_action')) return 'retargeting';
  return 'direct';
}

export function visualTypeFromAudienceType(type: AudienceType): VisualAudienceType {
  return type === 'direct'
    ? 'Direct'
    : type === 'retargeting'
      ? 'Retargeting'
      : 'Lookalike';
}

export function audienceTypeFromVisualType(type: VisualAudienceType): AudienceType {
  return type.toLowerCase() as AudienceType;
}

export function audienceIdForCreate(name: string, entropy?: string): string {
  const stem = name.toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 44) || 'audience';
  const suffix = (entropy
    ?? globalThis.crypto?.randomUUID?.()
    ?? Date.now().toString(36))
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 10);
  return `${stem}_${suffix || 'new'}`.slice(0, 64);
}
